import TestingSetupActions from './TestingSetupActions'
import { useGetTestRunNoticesQuery } from '../services/projectDataApi'
import MarkdownLite from './MarkdownLite'
import type { TestSession } from '../data/projectTypes'
import { useGetMonitoringStatusQuery } from '../services/sessionMonitoringApi'
import { Link } from './nav'
import SessionSummarizers from './SessionSummarizers'
import './OverviewTestingSummary.css'

export default function OverviewTestingSummary({ projectId, runs, runsLoading, showSetup = true }: {
  projectId: string; runs: TestSession[]; runsLoading: boolean; showSetup?: boolean
}) {
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const status = monitoring.currentData
  const monitoringActive = status?.enabled === true && status.configured
  const latest = runs[0]
  const notices = useGetTestRunNoticesQuery({ projectId, sessionId: latest?.id, versionId: latest?.version_id }, {
    skip: !latest || !status || monitoringActive,
    pollingInterval: 30000,
    skipPollingIfUnfocused: true,
  })
  const latestNotices = [...(notices.currentData?.notices ?? [])].reverse()
  const summary = latestNotices.find(notice => notice.kind === 'findings')
    ?? latestNotices.find(notice => notice.kind === 'finished')
  return <>
    {showSetup && <TestingSetupActions key={projectId} projectId={projectId} />}
    <section className="overview-testing" aria-label="Latest testing">
    <header className="overview-testing__header">
      <h2>Latest testing</h2>
      {status?.latest_run_id && <Link to={`/projects/${projectId}/test-runs/${status.latest_run_id}`}>View latest daily test run</Link>}
    </header>
    {monitoring.isLoading && <p role="status">Loading testing summary…</p>}
    {monitoring.isError && <p role="alert">Could not load monitoring status. <button onClick={() => monitoring.refetch()}>Retry</button></p>}
    {status && <>
      {monitoringActive ? <>
        {!status.has_sessions && <p role="status">Monitoring is enabled. Waiting for the first user sessions.</p>}
        <SessionSummarizers projectId={projectId} />
      </> : <>
        {runsLoading ? <p role="status">Loading latest test run…</p> : latest ? <div className="overview-testing__latest">
          <Link to={`/projects/${projectId}/test-runs/${latest.id}`}>{latest.title}</Link>
          <p><time dateTime={latest.started_at}>{new Date(latest.started_at).toLocaleString()}</time> · {latest.status}</p>
          <p>{latest.journeyRuns.length} journey runs · {latest.bugs.length} bugs</p>
          {notices.isFetching && !notices.currentData && <p role="status">Loading run summary…</p>}
          {notices.isError && <p role="alert">Could not load the run summary. <button onClick={() => notices.refetch()}>Retry</button></p>}
          {summary && <MarkdownLite text={summary.content} looseLists />}
        </div> : <p>No testing yet.</p>}
      </>}
    </>}
  </section></>
}
